import { useMemo, useState, type ReactNode } from 'react'

import type { SurveyFilterOption } from 'app/common/types/surveys'
import PlainModal, {
  PlainModalContent,
  PlainModalFooter,
  PlainModalHeader,
  type HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal'
import PlainCheckbox from 'app/common/ui-next/plain-checkbox'
import PlainInputSearch from 'app/common/ui-next/plain-input-search'
import PlainButton from 'app/common/ui-next/plain-button'
import ControlsBar from 'app/common/ui-next/controls-bar'
import ScrollbarsWShadow from 'app/common/ui-components/scrollbars-w-shadow'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import useSearch from 'app/common/hooks/useSearch'

import styles from './SchoolSelectionModal.module.scss'

const normalize = (value: string) => value.toLowerCase().replace(/\s|"|'|»|«|“|”/g, '')
const byTitle = (a: { title: string }, b: { title: string }) =>
  a.title.localeCompare(b.title, 'ru', { sensitivity: 'base' })

export const SchoolSelectionModal = ({
  schools,
  districts,
  selection,
  onChange,
  children,
}: {
  schools: Array<SurveyFilterOption>
  districts: Array<SurveyFilterOption>
  selection: Array<string>
  onChange: (ids: Array<string>) => void
  children: (toggle: HandleModalDisplayed) => ReactNode
}) => {
  const [draft, setDraft] = useState<Set<string>>(() => new Set(selection))
  const [search, handleSearch, clearSearch] = useSearch()
  const groups = useMemo(() => {
    const query = normalize(search)
    const grouped = new Map<string, Array<SurveyFilterOption>>()
    schools.forEach((school) => {
      const districtId = school.districtId || '0'
      const group = grouped.get(districtId) || []
      group.push(school)
      grouped.set(districtId, group)
    })
    return Array.from(grouped, ([id, items]) => {
      const title =
        districts.find((district) => district.id === id)?.title ||
        (id === '0' ? 'Без района' : `Район ${id}`)
      return {
        id,
        title,
        items: items
          .filter((school) => normalize(`${school.title}${title}`).includes(query))
          .sort(byTitle),
      }
    })
      .filter((group) => group.items.length > 0)
      .sort((a, b) => Number(a.id === '0') - Number(b.id === '0') || byTitle(a, b))
  }, [schools, districts, search])
  const allSelected = schools.length > 0 && schools.every((school) => draft.has(school.id))

  return (
    <PlainModal
      formId="surveySchool"
      className={styles.modal}
      controlBy={children}
      onDisplayStatusChange={(visible) => {
        if (!visible) return
        setDraft(new Set(selection))
        clearSearch()
      }}
      onSubmit={() => {
        onChange(
          allSelected ? [] : schools.filter((school) => draft.has(school.id)).map(({ id }) => id),
        )
        return true
      }}
    >
      <PlainModalHeader>{'Выбор заведения'}</PlainModalHeader>
      <PlainModalContent unscrollable>
        <PlainInputSearch
          className={styles.search}
          value={search}
          onChange={handleSearch}
          clearSearch={clearSearch}
          placeholder="Поиск"
          ContainerComponent="div"
        />
      </PlainModalContent>
      <PlainModalContent unscrollable>
        <PlainCheckbox
          name="surveySchoolAll"
          value="all"
          label="Выбрать все"
          checked={allSelected}
          disabled={!schools.length}
          onChange={() => setDraft(new Set(allSelected ? [] : schools.map(({ id }) => id)))}
        />
      </PlainModalContent>
      <PlainModalContent paddingFree unscrollable>
        <ScrollbarsWShadow autoHeight autoHeightMin={100} autoHeightMax={210}>
          {!groups.length && <EmptyWarn>{'Нет записей, удовлетворяющих условиям'}</EmptyWarn>}
          {groups.map((group) => (
            <section key={group.id} aria-labelledby={`survey-school-district-${group.id}`}>
              <h3 id={`survey-school-district-${group.id}`} className={styles.group}>
                {group.title}
              </h3>
              {group.items.map((school) => (
                <PlainCheckbox
                  key={school.id}
                  name="surveySchoolItem"
                  value={school.id}
                  label={school.title}
                  className={styles.item}
                  checked={draft.has(school.id)}
                  onChange={() => {
                    setDraft((current) => {
                      const next = new Set(current)
                      if (next.has(school.id)) next.delete(school.id)
                      else next.add(school.id)
                      return next
                    })
                  }}
                />
              ))}
            </section>
          ))}
        </ScrollbarsWShadow>
      </PlainModalContent>
      <PlainModalFooter>
        {(toggle) => (
          <ControlsBar middle>
            <PlainButton onClick={toggle} outline>
              {'Отмена'}
            </PlainButton>
            <PlainButton type="submit">{'Выбрать'}</PlainButton>
          </ControlsBar>
        )}
      </PlainModalFooter>
    </PlainModal>
  )
}
